import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getSkinId } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useResetSignUpError } from 'src/hooks/useResetSignupErrors';
import { getSkinBankAccountForms } from 'src/services/authorizationApi';
import { setBankAccountTypes, setError } from 'src/store/user/actions';
import { RootState } from 'src/types/store-types';
import * as Yup from 'yup';
import Dropdown from '../Dropdown';
import { Typography } from '../Typography';
import _styles from './styles.module.scss';

type IProps = {
  name: string;
  value: string;
  regexp: string;
  label: boolean;
  onChange: (e: any) => void;
  placeholder?: string;
  required?: boolean;
};

const BankName_Select: FC<IProps> = ({ onChange, name, value, required, label }) => {
  const { t }: Translation = useTranslation();
  const skinId = getSkinId();

  const _setErrorReq = useConnectedAction(setError);
  const _setBankAccountTypes = useConnectedAction(setBankAccountTypes);

  const resetSignUpError = useResetSignUpError();

  const { errors: signUpErrors } = useSelector((state: RootState) => state.user);

  const [bankNameVal, setBankNameVal] = useState<SelectOptionType | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [banksName, setBanksName] = useState<SelectOptionType[]>([]);

  const validationSchema = Yup.object().shape({
    bankName: Yup.string().required(t('requiredField')),
  });

  const handleValidate = async (value: string | number): Promise<void> => {
    try {
      await validationSchema.validate({ bankName: value }, { abortEarly: false });
      resetSignUpError('bankName');
    } catch (error: any) {
      if (error.name === 'ValidationError') {
        _setErrorReq({ ...signUpErrors, bankName: error.errors[0] });
      }
    }
  };

  const handleSelect = (option: SelectOptionType): void => {
    onChange({ target: { name, value: option.value } });
    setBankNameVal(option);
    handleValidate(option.value);
  };

  useEffect(() => {
    !value && isDropdownOpen && handleValidate(value);
  }, [isDropdownOpen]);

  useEffect(() => {
    getSkinBankAccountForms(skinId).then((response) => {
      if (response.success && response.result) {
        const { bankName, bankAccountType } = response.result?.banksInformation;
        const options: SelectOptionType[] = bankName?.list.map((bankName: string) => ({
          label: bankName,
          value: bankName,
        }));
        handleSelect(options[0]);
        _setBankAccountTypes(bankAccountType.list);
        setBanksName(options);
      }
    });
  }, []);

  return (
    <div className={_styles.bankName_select__wrapper}>
      {label && (
        <Typography className={_styles.label} variant={'body5'}>
          {t(label)} {required && <span>*</span>}
        </Typography>
      )}
      <Dropdown
        list={banksName}
        selected={bankNameVal}
        setIsDropdownOpen={setIsDropdownOpen}
        handleSelect={handleSelect}
        className={_styles.bankName_select}
        listClassName={_styles.bankName_select__list}
      />
      <span className={_styles.error}>{signUpErrors.bankName}</span>
    </div>
  );
};
export default BankName_Select;
